import React, { useEffect, useState } from 'react'
import Button from 'syngenta-react-components/dist/components/Button'
import {
  getMultiContractsByProdPlanIds,
  getMultiProductionPlans,
  getProgram,
  getProgramAssignments
} from '../../services/apiHelper'
import close from '../../assets/icons/close.svg'
import { Modal } from 'syngenta-react-components'
import _ from 'lodash'
import { generateExcel, generatePDF1 } from './GenerateDocument'
import Select from '../../dpe-usco/components/Select/Select'
import ContractTable from '../ContractDashboard/ContractTable'
import {
  convertToTitleCase,
  nullParser
} from '../../services/utils'
import moment from 'moment'
import { headerCell } from './components/headerCell'
import CloseButton from '../../components/subComponents/CloseButton'

// Helper function to map contract data
const mapContractData = (contract, programName) => ({
  contract_id: contract.contract_id,
  external_reference_id: contract.external_reference_id,
  contract_year: moment(contract.contract_start_execution).format('YYYY'),
  grower_name: `${nullParser(contract.contract_details.grower_details.first)} ${nullParser(
    contract.contract_details.grower_details.last
      ? contract.contract_details.grower_details.last
      : ''
  )}`,
  reseller: nullParser(contract.contract_details.reseller_details.reseller_name),
  contract_type: nullParser(contract.contract_details.display_details.contract_type),
  contract_status: nullParser(contract.contract_state),
  created_date: moment(contract.created).format('MM-DD-YYYY'),
  new_contract_id: nullParser(contract.new_contract_id),
  program_name: programName
})

const ProgramsReport = () => {
  const [reportPdfUrl, setReportPdfUrl] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [contractsData, setContractData] = useState([])
  const [contractStatus, setContractStatus] = useState('EMPTY')
  const [programDetails, setProgramDetails] = useState([])
  const [productionDetails, setProductionDetails] = useState([])
  const [selectedProgram, setSelectedProgram] = useState({})
  const [selectedProdPlan, setSelectedProdPlan] = useState({})
  const [sortOrder, setSortOrder] = useState('asc')
  const [sortByKey, setSortByKey] = useState()
  const [statusArr, setStatusArr] = useState([
    'pending',
    'pendinggrowersigned',
    'amendment_pending',
    'amendmentpendinggrowersigned',
    'completed',
    'amendment_completed',
    'voided'
  ])
  const [allContractData, setAllContractData] = useState([])
  const [isLoadExtraData, setIsLoadExtraData] = useState(false)
  const [contractDataOffset, setContractDataOffset] = useState(null)
  const [prodPlanStatus, setProdPlanStatus] = useState('')
  const limit = 20

  const handleClose = () => {
    setShowModal(false)
  }

  useEffect(() => {
    getProgram().then(res => {
      if (res?.data.programs.length) {
        const filteredData = res.data.programs
          .filter(x => x.is_active)
          .map(res => ({
            ...res,
            id: res.program_id,
            value: res.program_name
          }))
        setProgramDetails(filteredData)
      }
    })
  }, [])

  const processProgramAssignments = async assignments => {
    let allProdPlanIds = assignments.map(ele => ele.production_plan_id)
    allProdPlanIds = allProdPlanIds.join(',')

    try {
      const response = await getMultiProductionPlans(allProdPlanIds)
      const updatedProdPlanData = response.data.production_plans.map(
        item => ({
          ...item,
          id: item.production_plan_id,
          value: ` ${item.company_name} - ${item.inventory_code}`
        })
      )
      return updatedProdPlanData
    } catch (error) {
      return error
    }
  }

  useEffect(() => {
    const fetchProgramData = async () => {
      if (selectedProgram.program_id) {
        setProdPlanStatus('LOADING')
        setContractStatus('LOADING')
        try {
          const res = await getProgramAssignments(selectedProgram.program_id)
          if (res.data.assignments.length > 0) {
            const productionData = await processProgramAssignments(
              res.data.assignments
            )
            setProductionDetails(productionData)
            setProdPlanStatus('')
          } else {
            setContractStatus('EMPTY')
            setContractData([])
            setProdPlanStatus('')
          }
        } catch (error) {
          console.error('Error fetching program assignments:', error)
          setContractStatus('ERROR')
          setProdPlanStatus('')
          setContractData([])
        }
      }
    }
    fetchProgramData()
  }, [selectedProgram])

  const customSort = (data, dataKey, sortBy) => {
    if (data?.length && dataKey) {
      setSortOrder(sortBy)
      setSortByKey(dataKey)
      let newTableData = []
      if (dataKey === 'created_date' && sortBy === 'asc') {
        newTableData = [...data].sort(function (a, b) {
          return new Date(a.created_date) - new Date(b.created_date)
        })
      } else if (dataKey === 'created_date') {
        newTableData = [...data].sort(function (a, b) {
          return new Date(b.created_date) - new Date(a.created_date)
        })
      } else if (sortBy === 'asc') {
        newTableData = [...data].sort((a, b) => {
          return a[dataKey]?.toLowerCase() > b[dataKey]?.toLowerCase() ? 1 : -1
        })
      } else {
        newTableData = [...data].sort((a, b) => {
          return a[dataKey]?.toLowerCase() > b[dataKey]?.toLowerCase() ? -1 : 1
        })
      }
      return newTableData
    }
    return data || []
  }

  useEffect(() => {
    const filteredData = allContractData.filter(ele => {
      return statusArr.some(f => {
        return f === ele.contract_status
      })
    })

    const data = customSort(filteredData, sortByKey, sortOrder)
    setContractData(data)
  }, [statusArr, sortByKey, sortOrder])

  useEffect(() => {
    const fetchContracts = async data => {
      setContractStatus('LOADING')
      const reqPayload = {
        production_plan_ids: data
      }
      try {
        const res = await getMultiContractsByProdPlanIds(reqPayload, limit, 0)
        if (res.data.contracts.length == 0) {
          setContractStatus('EMPTY')
          setContractData([])
          setAllContractData([])
        } else {
          const updatedData = res.data.contracts.map(contract =>
            mapContractData(contract, selectedProgram.program_name)
          )
          const filteredData = updatedData.filter(ele => {
            return statusArr.some(f => {
              return f === ele.contract_status
            })
          })
          setContractData(customSort(filteredData, sortByKey, sortOrder))
          setAllContractData(customSort(updatedData, sortByKey, sortOrder))
          setContractDataOffset(1)
          setContractStatus('')
        }
      } catch (error) {
        console.error('Error fetching program assignments:', error)
        setContractStatus('ERROR')
        setContractData([])
      }
    }

    if (selectedProdPlan && selectedProdPlan.production_plan_id) {
      fetchContracts([selectedProdPlan.production_plan_id])
    } else if (productionDetails.length > 0) {
      let allProdPlanIds = productionDetails.map(
        ele => ele.production_plan_id
      )
      fetchContracts(allProdPlanIds)
    }
  }, [selectedProdPlan, selectedProgram, productionDetails])

  const getMoreContracts = () => {
    if (
      contractDataOffset !== null &&
      contractsData.length > 0 &&
      contractStatus !== 'LOADING'
    ) {
      const fetchContracts = async data => {
        let offset = limit * contractDataOffset + 1
        setContractDataOffset(contractDataOffset + 1)
        setIsLoadExtraData(true)
        const reqPayload = {
          production_plan_ids: data
        }
        try {
          const res = await getMultiContractsByProdPlanIds(
            reqPayload,
            limit,
            offset
          )
          if (res.data.contracts.length == 0) {
            setContractDataOffset(null)
            setIsLoadExtraData(false)
          } else {
            const updatedData = res.data.contracts.map(contract =>
              mapContractData(contract, selectedProgram.program_name)
            )
            const filteredData = updatedData.filter(ele => {
              return statusArr.some(f => {
                return f === ele.contract_status
              })
            })

            setContractData([...contractsData, ...filteredData])
            setAllContractData([...allContractData, ...filteredData])
            setIsLoadExtraData(false)
          }
        } catch (error) {
          setContractDataOffset(null)
          setIsLoadExtraData(false)
        }
      }

      if (selectedProdPlan && selectedProdPlan.production_plan_id) {
        fetchContracts([selectedProdPlan.production_plan_id])
      } else if (productionDetails.length > 0) {
        let allProdPlanIds = productionDetails.map(
          ele => ele.production_plan_id
        )
        fetchContracts(allProdPlanIds)
      }
    }
  }

  const callToDownloadExlSheet = () => {
    const table = '.table'
    const header = '.tableHeader > div'
    const body = '.tableBody > div'
    generateExcel(table, header, body)
  }

  const handleProgramNameSelection = option => {
    setSelectedProdPlan(null)
    setProductionDetails([])
    setSelectedProgram(option)
    setContractData([])
  }
  const handleProgramYearSelection = option => {
    setSelectedProdPlan(option)
  }

  const structure = {
    headers: [
      {
        value: 'Created Date',
        format: headerCell
      },
      {
        value: 'Contract ID',
        format: headerCell
      },
      {
        value: 'Grower',
        format: headerCell
      },
      {
        value: 'Reseller',
        format: headerCell
      },
      {
        value: 'Contract type',
        format: headerCell
      },
      {
        value: 'Contract Status',
        format: headerCell
      },
      {
        value: 'Program Name',
        format: headerCell
      }
    ],
    dataKeys: [
      'created_date',
      'new_contract_id',
      'grower_name',
      'reseller',
      'contract_type',
      'contract_status',
      'program_name'
    ],
    rowFormat: {
      contract_year: {
        format: contract => {
          return <span>{contract}</span>
        }
      },
      contract_status: {
        format: status => {

          if (status === 'amendment_pending') {
            status = 'amendment pending'
          } else if (status === 'amendment_completed') {
            status = 'amendment completed'
          } else if (status === 'amendmentpendinggrowersigned') {
            status = 'amd pend grower signed'
          } else if (status === 'pendinggrowersigned') {
            status = 'pending grower signed'
          }

          return convertToTitleCase(status)
        }
      },
      contract_type: {
        format: (type, _row) => {
          return convertToTitleCase(type)
        }
      },
      program_name: {
        customStyle: '',
        format: (name, _row) => {
          return convertToTitleCase(name)
        }
      },
      reseller: {
        format: (reseller, _row) => {
          return convertToTitleCase(reseller)
        }
      }
    }
  }

  useEffect(() => {
    setReportPdfUrl('')
    const header = 'Program Report'
    const subHeader = 'Contracts'
    const pdf = generatePDF1(contractsData, structure, header, subHeader)
    setReportPdfUrl(pdf)
  }, [showModal, contractsData])

  return (
    <>
      <div className="m25">
        <div className="program-report-container">
          <h4>Program Report</h4>
          <div className="search-box">
            <div className="search-lists">
              <div className="selectBars">
                <label>Program Name</label>
                <Select
                  placeholder="Select Program"
                  value={selectedProgram && selectedProgram.program_name}
                  className="mt10 mb10"
                  handleSelection={handleProgramNameSelection}
                  options={programDetails}
                  disabled={
                    programDetails.length === 0 || prodPlanStatus === 'LOADING'
                  }
                />
              </div>
              {prodPlanStatus === 'LOADING' ? (
                <div className="loading" />
              ) : (
                <div className="selectBar2">
                  <label>Production Plan</label>
                  <Select
                    placeholder="Select Year"
                    value={
                      selectedProdPlan
                        ? selectedProdPlan.production_plan_year
                        : ''
                    }
                    className="mt10 mb10"
                    handleSelection={handleProgramYearSelection}
                    options={productionDetails}
                    disabled={productionDetails.length === 0}
                  />
                </div>
              )}
            </div>
            <div className="pdf-btn">
              <Button
                onClick={callToDownloadExlSheet}
                customClassName="mr10"
                backgroundColor={
                  selectedProgram.program_name ? '#238661' : '#8FC0AE'
                }
                textColor={'#FFFFFF'}
                disabled={
                  contractsData.length === 0 || contractStatus === 'LOADING'
                }
                text="Download as Excel"
              />
              <Button
                onClick={e => setShowModal(true)}
                customClassName="mr10"
                backgroundColor={
                  selectedProgram.program_name ? '#238661' : '#8FC0AE'
                }
                textColor={'#FFFFFF'}
                disabled={
                  contractsData.length === 0 || contractStatus === 'LOADING'
                }
                text="Download as PDF"
              />
            </div>
          </div>
          <h4>Contracts</h4>
          <div className="">
            <ContractTable
              setStatusArr={setStatusArr}
              isReportPage={true}
              data={contractsData}
              contractNavigates={true}
              showCreateContract={false}
              status={contractStatus}
              setSortOrder={setSortOrder}
              setSortByKey={setSortByKey}
              customSort={customSort}
              prompt="No data"
              displayCreatedDate={true}
              filterByStatus={true}
              loadingData={false}
              onScrollAPI={getMoreContracts}
              isLoadExtraData={isLoadExtraData}
            />
          </div>
        </div>
      </div>

      <Modal autoHeight show={showModal} handleClose={handleClose}>
        <div
          className="modalContainer"
          style={{
            display: showModal ? 'block' : 'none',
            backgroundColor: 'rgba(0, 0, 0, 1%'
          }}
        >
          <div
            className="aHModalChild posr t100"
            style={{ maxWidth: '1200px' }}
          >
            <div className="modalStyle w100% tal h100% bgc-white bd1-s-lightgray bxshmodal bdrs-4 mb40">
              <div className="cancelDiv">
                <div>Program Report</div>
                <CloseButton closeAction={handleClose} src={close} />
              </div>

              {reportPdfUrl && (
                <object
                  data={reportPdfUrl}
                  type="application/pdf"
                  className="pdf-container"
                  name="report"
                  aria-label="PDF Report"
                >
                  <p>This browser does not support PDFs.</p>
                </object>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
export default ProgramsReport
